import React, { useState, useEffect } from 'react';
import { Star, MessageCircle, Send, BarChart3, Clock, CheckCircle2, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const SatisfactionAdminPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [resending, setResending] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/satisfaction/admin/dashboard`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setData(res.data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resendEmail = async (surveyId) => {
    setResending(surveyId);
    try {
      await axios.post(`${API}/satisfaction/admin/resend/${surveyId}`, {}, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      toast.success('Email de rappel envoye');
    } catch { toast.error('Erreur d\'envoi'); }
    finally { setResending(null); }
  };

  const filteredSurveys = data?.surveys?.filter(s => {
    if (tab === 'completed') return s.status === 'completed';
    if (tab === 'pending') return s.status === 'pending';
    return true;
  }) || [];

  const renderStars = (rating) => {
    if (!rating) return <span className="text-xs text-slate-400">--</span>;
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
        ))}
      </div>
    );
  };

  if (loading) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-400">{t('common.loading')}</p>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="satisfaction-title">{t('satisfaction.title')}</h1>
            <p className="text-sm text-slate-500 mt-1">Enquetes envoyees automatiquement apres facturation</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-satisfaction">
            <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
          </Button>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4" data-testid="stat-total-surveys">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Total enquetes</p>
            <p className="text-2xl font-bold font-mono text-slate-900">{data?.total || 0}</p>
          </Card>
          <Card className="p-4" data-testid="stat-completed-surveys">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Reponses</p>
            <p className="text-2xl font-bold font-mono text-emerald-600">{data?.completed || 0}</p>
            <p className="text-xs text-slate-400">{data?.response_rate || 0}% taux de reponse</p>
          </Card>
          <Card className="p-4" data-testid="stat-avg-rating">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Note moyenne</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold font-mono text-amber-600">{data?.avg_rating || '--'}</p>
              <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
            </div>
          </Card>
          <Card className="p-4" data-testid="stat-pending-surveys">
            <p className="text-xs text-slate-500 uppercase tracking-wider">{t('common.pending')}</p>
            <p className="text-2xl font-bold font-mono text-blue-600">{data?.pending || 0}</p>
          </Card>
        </div>

        {/* Rating distribution */}
        {data?.completed > 0 && (
          <Card className="p-4" data-testid="rating-distribution">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Distribution des notes
            </h3>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(star => {
                const count = data?.rating_distribution?.[star] || 0;
                const pct = data?.completed > 0 ? Math.round(count / data.completed * 100) : 0;
                return (
                  <div key={star} className="flex items-center gap-3">
                    <div className="flex items-center gap-1 w-16">
                      <span className="text-sm font-mono text-slate-600">{star}</span>
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    </div>
                    <div className="flex-1 bg-slate-100 rounded-full h-3 overflow-hidden">
                      <div className={`h-full rounded-full ${star >= 4 ? 'bg-emerald-500' : star === 3 ? 'bg-amber-400' : 'bg-red-400'}`}
                        style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-xs font-mono text-slate-500 w-16 text-right">{count} ({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {[
            { key: 'all', label: `Toutes (${data?.total || 0})` },
            { key: 'completed', label: `Reponses (${data?.completed || 0})` },
            { key: 'pending', label: `En attente (${data?.pending || 0})` },
          ].map(t => (
            <button key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-t transition-colors ${
                tab === t.key ? 'bg-white border border-b-0 border-slate-200 text-slate-900' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Survey list */}
        {filteredSurveys.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-sm text-slate-400">Aucune enquete de satisfaction</p>
            <p className="text-xs text-slate-300 mt-1">Les enquetes sont creees automatiquement lors de la facturation</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredSurveys.map(s => (
              <Card key={s.id} className="p-4" data-testid={`survey-${s.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-sm font-semibold text-slate-900">{s.client_name}</p>
                      {s.status === 'completed' ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Repondu
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full flex items-center gap-1">
                          <Clock className="w-3 h-3" /> En attente
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      {s.vehicle_info && <span>{s.vehicle_info}</span>}
                      {s.invoice_number && <span className="font-mono">Facture: {s.invoice_number}</span>}
                      {s.created_at && <span>{new Date(s.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    </div>
                    {s.status === 'completed' && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          {renderStars(s.rating)}
                          <span className="text-sm font-mono font-bold text-slate-700">{s.rating}/5</span>
                        </div>
                        {s.comment && (
                          <div className="flex items-start gap-2 mt-1">
                            <MessageCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                            <p className="text-sm text-slate-600 italic">"{s.comment}"</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {s.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resendEmail(s.id)}
                      disabled={resending === s.id}
                      data-testid={`resend-${s.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1" />
                      {resending === s.id ? 'Envoi...' : 'Relancer'}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SatisfactionAdminPage;
